import { Action, action, ThunkOn, thunkOn, State, Thunk, thunk } from 'easy-peasy';
import { set } from 'lodash';
import { StoreState } from './store';
import { UserSettings, StorageKeys, SubtitleTextSizes, SubtitleTextWeights, SubtitleTextColors } from '../typings';

export interface SettingsState {
  users: { [guid: string]: UserSettings };
  user: UserSettings | null;
  setUsers: Action<SettingsState, { [guid: string]: UserSettings }>;
  setUser: Action<SettingsState, UserSettings | null>;
  load: Thunk<SettingsState, void, any, StoreState>;
  save: Thunk<SettingsState, void, any, StoreState>;
  reset: Action<SettingsState, void>;
  setAudioTrack: Thunk<SettingsState, AudioTrack, any, StoreState>;
  setSubtitleTrack: Thunk<SettingsState, TextTrack | null, any, StoreState>;
  setSubtitleTextSize: Thunk<SettingsState, SubtitleTextSizes, any, StoreState>;
  setSubtitleTextWeight: Thunk<SettingsState, SubtitleTextWeights, any, StoreState>;
  setSubtitleTextColor: Thunk<SettingsState, SubtitleTextColors, any, StoreState>;
  setVolumeLevel: Thunk<SettingsState, UserSettings['volumeLevel'], any, StoreState>;
  onVideoLoaded: ThunkOn<SettingsState, AudioTrack, StoreState>;
  onPlayerDestroy: ThunkOn<SettingsState, AudioTrack, StoreState>;
  onSetUsers: ThunkOn<SettingsState, void, StoreState>;
}

const initialState: State<SettingsState> = {
  users: {},
  user: null,
};

export const settingsState: SettingsState = {
  /***********
   * State
   ***********/
  ...initialState,

  /***********
   * Actions
   ***********/
  load: thunk((actions, _, { getStoreState, getStoreActions }) => {
    const storeState = getStoreState();
    const storeActions = getStoreActions();

    if (!storeState.config.userGuid) return;

    const data = JSON.parse(localStorage.getItem(StorageKeys.SETTINGS) || 'null');

    const users = (data?.users || {}) as SettingsState['users'];
    const user = users[String(storeState.config.userGuid)] || null;

    actions.setUsers(users);
    actions.setUser(user);

    if (user?.subtitleTextColor) {
      storeActions.subtitles.setSubtitleTextColor(user.subtitleTextColor);
    }

    if (user?.subtitleTextSize) {
      storeActions.subtitles.setSubtitleTextSize(user.subtitleTextSize);
    }

    if (user?.subtitleTextWeight) {
      storeActions.subtitles.setSubtitleTextWeight(user.subtitleTextWeight);
    }

    if (user?.volumeLevel) {
      // Save the player volume in percentage interval
      storeActions.player.setVolume(user.volumeLevel * 100);
    }
  }),

  save: thunk((_, __, { getState, getStoreState }) => {
    const state = getState();
    const storeState = getStoreState();

    if (!storeState.config.userGuid) return;

    localStorage.setItem(StorageKeys.SETTINGS, JSON.stringify(state));
  }),

  reset: action((state) => {
    Object.assign(state, { ...initialState });
  }),

  setUsers: action((state, usersSettings) => {
    state.users = usersSettings;
  }),

  setUser: action((state, userSettings) => {
    state.user = userSettings;
  }),

  /*************
   * Listeners
   *************/
  onVideoLoaded: thunkOn(
    function observer(_, storeActions) {
      return [storeActions.player.loadVideo];
    },
    function handler(actions) {
      actions.load();
    }
  ),

  onPlayerDestroy: thunkOn(
    function observer(_, storeActions) {
      return [storeActions.player.destroy.startType];
    },
    function handler(actions) {
      actions.reset();
    }
  ),

  /**
   * When the users settings list state is updated,
   * also update the current user settings.
   */
  onSetUsers: thunkOn(
    function observer(actions) {
      return [actions.setUsers];
    },
    function handler(actions, _, { getState, getStoreState }) {
      const state = getState();
      const storeState = getStoreState();

      const user = state.users[String(storeState.config.userGuid)] || null;
      actions.setUser(user);
    }
  ),

  setAudioTrack: thunk((actions, audioTrack, { getState, getStoreState }) => {
    const { config } = getStoreState();
    let { users } = getState();
    const userGuid = String(config.userGuid);

    if (!audioTrack) return;

    set(users, `['${userGuid}'].audio`, audioTrack.label);
    actions.setUsers(users);
    actions.save();
  }),

  setSubtitleTrack: thunk((actions, subtitleTrack, { getState, getStoreState }) => {
    const { config } = getStoreState();
    let { users } = getState();
    const userGuid = String(config.userGuid);

    set(users, `['${userGuid}'].subtitle`, subtitleTrack?.label ?? null);
    actions.setUsers(users);
    actions.save();
  }),

  setSubtitleTextSize: thunk((actions, subtitleTextSize, { getState, getStoreState }) => {
    const { config } = getStoreState();
    let { users } = getState();
    const userGuid = String(config.userGuid);

    set(users, `['${userGuid}'].subtitleTextSize`, subtitleTextSize);
    actions.setUsers(users);
    actions.save();
  }),

  setSubtitleTextWeight: thunk((actions, subtitleTextWeight, { getState, getStoreState }) => {
    const { config } = getStoreState();
    let { users } = getState();
    const userGuid = String(config.userGuid);

    set(users, `['${userGuid}'].subtitleTextWeight`, subtitleTextWeight);
    actions.setUsers(users);
    actions.save();
  }),

  setSubtitleTextColor: thunk((actions, subtitleTextColor, { getState, getStoreState }) => {
    const { config } = getStoreState();
    let { users } = getState();
    const userGuid = String(config.userGuid);

    set(users, `['${userGuid}'].subtitleTextColor`, subtitleTextColor);
    actions.setUsers(users);
    actions.save();
  }),

  setVolumeLevel: thunk((actions, volumeLevel, { getState, getStoreState }) => {
    const { config } = getStoreState();
    let { users } = getState();
    const userGuid = String(config.userGuid);

    set(users, `['${userGuid}'].volumeLevel`, volumeLevel);
    actions.setUsers(users);
    actions.save();
  }),
};
